import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  lamaForms: [],
  currentLamaForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all LAMA Forms
export const fetchLamaForms = createAsyncThunk(
  'lamaForms/fetchLamaForms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/lama-forms');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch LAMA forms'
      );
    }
  }
);

// Fetch a single LAMA Form
export const fetchLamaForm = createAsyncThunk(
  'lamaForms/fetchLamaForm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/lama-forms/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch LAMA form'
      );
    }
  }
);

// Create a new LAMA Form
export const createLamaForm = createAsyncThunk(
  'lamaForms/createLamaForm',
  async (lamaData, { rejectWithValue }) => {
    try {
      const response = await api.post('/lama-forms', lamaData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create LAMA form'
      );
    }
  }
);

// Update an existing LAMA Form (by ID)
export const updateLamaForm = createAsyncThunk(
  'lamaForms/updateLamaForm',
  async ({ case_id, lamaData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/lama-forms/case/${case_id}`, lamaData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update LAMA form'
      );
    }
  }
);

// Delete a LAMA Form
export const deleteLamaForm = createAsyncThunk(
  'lamaForms/deleteLamaForm',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/lama-forms/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete LAMA form'
      );
    }
  }
);

// ==================== Slice ====================
const lamaFormsSlice = createSlice({
  name: 'lamaForms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentLamaForm: (state, action) => {
      state.currentLamaForm = action.payload;
    },
    clearCurrentLamaForm: (state) => {
      state.currentLamaForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchLamaForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLamaForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lamaForms = action.payload;
      })
      .addCase(fetchLamaForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchLamaForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLamaForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentLamaForm = action.payload;
      })
      .addCase(fetchLamaForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createLamaForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createLamaForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lamaForms.unshift(action.payload);
      })
      .addCase(createLamaForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateLamaForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLamaForm.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.lamaForms.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.lamaForms[index] = updated;
        }

        if (state.currentLamaForm?.id === updated.id) {
          state.currentLamaForm = updated;
        }
      })
      .addCase(updateLamaForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteLamaForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLamaForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lamaForms = state.lamaForms.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentLamaForm?.id === action.payload) {
          state.currentLamaForm = null;
        }
      })
      .addCase(deleteLamaForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentLamaForm,
  clearCurrentLamaForm,
} = lamaFormsSlice.actions;

export default lamaFormsSlice.reducer;
